/* ============================================================
   Dathent — ContactOptionsModal (.upm-* chrome + .com-* content)
   Shared "how would you like to reach us" chooser: Discord community
   (primary — fastest replies) + a same-page email mini-form that posts
   to the existing public contact pipeline (email + Telegram + D1 — see
   Plans.jsx's former inline Enterprise form, which this replaces/reuses).
   Chrome (scrim, card, Esc, focus trap, close X) is cloned from
   UpgradeModal.jsx so it renders with the exact same feel; new visual
   vocabulary lives under .com- in styles/upgrade-modal.css.
   Used by: Plans.jsx (Enterprise strip, source='app-plans-enterprise')
   and Support.jsx (Help Center "Contact support", source='app-help').
   ============================================================ */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from './Icons.jsx';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api/client.js';

const DISCORD_INVITE_URL = 'https://discord.gg/YN4g3ThGqM';

// Discord brand mark is a FILLED shape, so it can't live in the stroke-based
// Icon set — copied verbatim from AppShell/Sidebar.jsx's DiscordIcon (same
// invite, nominative use: links to our own community server).
const DiscordIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.317 4.3698a19.7913 19.7913 0 0 0-4.8851-1.5152.0741.0741 0 0 0-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 0 0-.0785-.037 19.7363 19.7363 0 0 0-4.8852 1.515.0699.0699 0 0 0-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 0 0 .0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 0 0 .0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 0 0-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 0 1-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 0 1 .0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 0 1 .0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 0 1-.0066.1276 12.2986 12.2986 0 0 1-1.873.8914.0766.0766 0 0 0-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 0 0 .0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 0 0 .0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 0 0-.0312-.0286ZM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189Zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" /></svg>
);

const isValidEmail = (value) => /\S+@\S+\.\S+/.test(value);

export default function ContactOptionsModal({ open, onClose, source = 'app-contact' }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const ctaRef = useRef(null);
  const prevFocusRef = useRef(null);

  // Reset + focus the primary (Discord) CTA on open; restore focus to the
  // trigger on close — same pattern as UpgradeModal.
  useEffect(() => {
    if (!open) return undefined;
    prevFocusRef.current = document.activeElement;
    setError(null);
    setSent(false);
    setBusy(false);
    setMessage('');
    setEmail(user?.email || '');
    const raf = requestAnimationFrame(() => ctaRef.current?.focus());
    return () => {
      cancelAnimationFrame(raf);
      const prev = prevFocusRef.current;
      if (prev && typeof prev.focus === 'function') prev.focus();
    };
  }, [open, user?.email]);

  // Esc to close.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleDiscord = () => {
    window.open(DISCORD_INVITE_URL, '_blank', 'noopener');
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const trimmedEmail = email.trim();
    const trimmedMessage = message.trim();
    if (!trimmedEmail || !isValidEmail(trimmedEmail) || !trimmedMessage) {
      setError(t('contactModal.validationError', { defaultValue: 'Please enter a valid email and a message.' }));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      // Same public contact endpoint as the former Plans.jsx Enterprise form
      // (email + Telegram + D1 delivery) — just a different `source` tag.
      await api.request('POST', '/public/contact', {
        name: user?.name || 'App user',
        email: trimmedEmail,
        message: trimmedMessage,
        source,
      });
      setSent(true);
    } catch (err) {
      setError(err?.message || t('contactModal.error', { defaultValue: "Couldn't send — please try again." }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="upm-scrim"
      role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="upm-card com-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="com-title"
      >
        <button type="button" className="upm-close" onClick={() => onClose?.()} aria-label={t('upgradeModal.close', 'Close')}>
          <Icon name="x" size={18} />
        </button>

        <div className="com-head">
          <h2 id="com-title" className="upm-title">{t('contactModal.title', 'Talk to us')}</h2>
        </div>

        <div className="com-grid">
          <div className="com-opt com-opt-primary">
            <div className="com-opt-head">
              <span className="com-opt-icon com-opt-icon-discord" aria-hidden="true"><DiscordIcon size={20} /></span>
              <h3 className="com-opt-title">{t('contactModal.discordTitle', 'Discord community')}</h3>
              <p className="com-opt-body">{t('contactModal.discordBody', 'Fastest replies — the team and community are there daily.')}</p>
            </div>
            <button type="button" className="com-opt-cta com-opt-cta-gold" onClick={handleDiscord} ref={ctaRef}>
              <DiscordIcon size={15} /> {t('contactModal.discordCta', 'Open Discord')}
            </button>
          </div>

          <div className="com-opt">
            <div className="com-opt-head">
              <span className="com-opt-icon" aria-hidden="true"><Icon name="mail" size={17} /></span>
              <h3 className="com-opt-title">{t('contactModal.emailTitle', 'Write to us')}</h3>
            </div>

            {sent ? (
              <div className="com-sent" role="status">
                <Icon name="check" size={13} stroke={2.4} aria-hidden="true" />
                <span>{t('contactModal.sent', "Sent — we'll get back to you by email.")}</span>
              </div>
            ) : (
              <form className="com-form" onSubmit={handleEmailSubmit}>
                <input
                  type="email"
                  className="com-input"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('contactModal.emailPh', 'Your email')}
                  autoComplete="email"
                />
                <textarea
                  className="com-textarea"
                  required
                  rows={3}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('contactModal.messagePh', 'How can we help?')}
                />
                {error && (
                  <div className="com-err" role="alert">
                    <Icon name="alert" size={13} aria-hidden="true" />
                    <span>{error}</span>
                  </div>
                )}
                <button type="submit" className="com-opt-cta" disabled={busy}>
                  {busy ? <span className="upm-spinner" aria-hidden="true" /> : <Icon name="send" size={14} aria-hidden="true" />}
                  {t('contactModal.emailCta', 'Send message')}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
